/* eslint-disable @typescript-eslint/no-explicit-any */
import Image from 'next/image'
import React, { forwardRef } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { styled, useMediaQuery, useTheme } from '@mui/material'

export type LogoVariant = 'desktop' | 'mobile' | 'desktop-nomascot'

export type LogoProps = Omit<React.ComponentProps<typeof Image>, 'src' | 'alt'> & {
  onClick?: () => void
  variant?: LogoVariant
  colorVariant?: 'light' | 'dark' | 'color'
}

const sizeMap: Record<LogoVariant, [number, number]> = {
  'desktop': [64, 64],
  'desktop-nomascot': [64, 64],
  'mobile': [44, 44],
}

const colorSizeMap: Record<'desktop' | 'mobile', [number, number]> = {
  'desktop': [196, 60],
  'mobile': [137, 42],
}

const LOGO_PATHS = {
  dark: '/logodark.svg',
  light: '/logolight.svg',
  color: '/pixieworld-logo-full.png',
}

export const PixieWorldLogo = forwardRef<HTMLImageElement, LogoProps>(
  ({ onClick, variant, colorVariant = 'dark', ...props }, ref) => {
    const theme = useTheme()
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

    const isColor = colorVariant === 'color'
    const defaultVariant: LogoVariant = isDesktop ? 'desktop' : 'mobile'
    const resolvedVariant = variant || defaultVariant

    const [width, height] = isColor
      ? colorSizeMap[isDesktop ? 'desktop' : 'mobile']
      : sizeMap[resolvedVariant]

    const src = LOGO_PATHS[colorVariant]

    return (
      <LogoContainer $clickable={!!onClick} onClick={onClick}>
        <StyledImage
          ref={ref}
          priority
          src={src}
          width={width}
          height={height}
          alt="Pixie World Logo"
          loading="eager"
          {...props}
        />
        {!isColor && <LogoText $colorVariant={colorVariant}>Pixie World</LogoText>}
      </LogoContainer>
    )
  },
)

// Keep backwards compatibility alias
export const AnimeTownLogo = PixieWorldLogo

const LogoContainer = styled('div')<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
`

const StyledImage = styled(Image)`

`

const LogoText = styled('p')<{ $colorVariant?: 'light' | 'dark' | 'color' }>`
  font-family: ${props => props.theme.typography.fontFamily};
  font-weight: 600;
  font-size: ${pxToRem(16)};
  margin-left: ${pxToRem(8)};
  color: ${props => props.$colorVariant === 'light'
    ? props.theme.palette.custom.footer.text
    : props.theme.palette.primary.main};
  text-decoration: none !important;

  ${props => props.theme.breakpoints.up('md')} {
    font-size: ${pxToRem(30)};
  }
`